import React, { useState, useEffect, useCallback, useContext } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BsStarFill, BsCartPlus, BsBagCheck, BsArrowRight, BsTagsFill, BsStars } from "react-icons/bs";
import { SERVER_PATH } from "../../config/env";
import { UserContext } from "../../context/userContext";
import { getLocalStorage } from "../../utils/helper";
import { userInfo } from "../../utils/appInfo";
import { successToaster, errorToaster } from "../../utils";
import "./uniqueProducts.css";

import { formatProductText } from "../../utils/productTextFormatter";

export default function UniqueProductsHome() {
  const navigate = useNavigate();
  const { userData, updateUserData } = useContext(UserContext);
  const isLogin = getLocalStorage("uid") ? true : false;

  const getUserId = () => {
    const user = userInfo();
    return user?.email || getLocalStorage("uid") || (userData && userData.email) || "";
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUniqueProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER_PATH}get/unique_products.php`, {
        params: {
          status: 1
        }
      });

      if (res.data && res.data.status === "success") {
        setProducts(res.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.log("Error fetching unique products for homepage:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUniqueProducts();
  }, [fetchUniqueProducts]);

  // Add to Cart Handler
  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    if (!isLogin) {
      navigate(`/profile/${product.product_id}`);
      return;
    }

    const userId = getUserId();
    if (!userId) {
      errorToaster({ msg: "Please log in to add products to cart" });
      navigate(`/profile/${product.product_id}`);
      return;
    }

    axios
      .post(`${SERVER_PATH}post/cart.php`, {
        product_id: product.product_id,
        user_id: userId,
        quantity: 1
      })
      .then(() => {
        successToaster({ msg: `"${product.product_name}" added to cart!` });
        if (userData?.cart) {
          updateUserData({ cart: [...userData.cart, product] });
        }
      })
      .catch((err) => {
        console.log(err);
        errorToaster({ msg: "Failed to add product to cart." });
      });
  };

  // Buy Now Handler
  const handleBuyNow = (e, product) => {
    e.stopPropagation();
    if (!isLogin) {
      navigate(`/profile/${product.product_id}`);
      return;
    }

    const userId = getUserId();
    if (!userId) {
      errorToaster({ msg: "Please log in to buy products" });
      navigate(`/profile/${product.product_id}`);
      return;
    }

    axios
      .post(`${SERVER_PATH}post/cart.php`, {
        product_id: product.product_id,
        user_id: userId,
        quantity: 1
      })
      .then(() => {
        navigate(`/cart/${product.product_id}`);
      })
      .catch((err) => {
        console.log(err);
        errorToaster({ msg: "Failed to add product to cart." });
      });
  };

  const handleViewDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (loading) {
    return (
      <section className="unique-home-section py-5 text-center" style={{ backgroundColor: "var(--background)" }}>
        <Container>
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading unique products...</span>
          </div>
        </Container>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  // Show up to 8 top unique products on the home page
  const displayProducts = products.slice(0, 8);

  return (
    <section className="unique-home-section py-5" style={{ backgroundColor: "var(--background)" }}>
      <Container>
        <div className="row text-center mb-4">
          <div className="col-lg-7 m-auto">
            <span className="badge bg-success text-white px-3 py-2 rounded-pill fw-bold text-uppercase mb-2 d-inline-flex align-items-center gap-1 shadow-sm">
              <BsStars /> Handpicked Selection
            </span>
            <h2 className="h2 fw-bold text-dark mb-2">Unique Products</h2>
            <p className="text-muted fs-6 mb-0">
              Discover our signature herbal & organic creations, crafted with pure traditional ingredients
            </p>
          </div>
        </div>

        <Row className="g-4">
          {displayProducts.map((item) => {
            const regularPrice = parseInt(item.price) || 0;
            const offerPrice = parseInt(item.offer_price) || 0;
            const activePrice = offerPrice > 0 ? offerPrice : regularPrice;
            const hasDiscount = offerPrice > 0 && regularPrice > offerPrice;
            const discountPercentage = hasDiscount ? Math.round(((regularPrice - offerPrice) / regularPrice) * 100) : 0;

            return (
              <Col key={item.unique_id || item.product_id} col={12} sm={6} md={4} lg={3}>
                <Card className="unique-product-card shadow-sm border-0 h-100">
                  <div className="unique-card-img-wrapper" onClick={() => handleViewDetails(item.product_id)}>
                    {item.featured == 1 && (
                      <span className="badge-featured">★ Featured</span>
                    )}

                    {item.highlight_text && (
                      <span className="badge-highlight">
                        <BsTagsFill className="me-1" /> {formatProductText(item.highlight_text)}
                      </span>
                    )}

                    {hasDiscount && (
                      <span className="badge-discount">{discountPercentage}% OFF</span>
                    )}

                    <img
                      src={item.img1 || "placeholder.png"}
                      alt={formatProductText(item.product_name)}
                      className="unique-card-img"
                      loading="lazy"
                    />
                  </div>

                  <div className="unique-card-body d-flex flex-column p-3">
                    <div className="unique-card-category text-success small fw-bold text-uppercase mb-1">
                      {item.category}
                    </div>
                    <h5 className="unique-card-title h6 fw-bold mb-2 text-dark" onClick={() => handleViewDetails(item.product_id)}>
                      {formatProductText(item.product_name)}
                    </h5>

                    <div className="unique-card-rating mb-2">
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarFill />
                      <span className="text-muted ms-1 small fw-semibold">(4.9)</span>
                    </div>

                    <div className="unique-price-box d-flex align-items-baseline gap-2 mb-3">
                      <span className="unique-offer-price fw-bold text-success fs-5">₹{activePrice}</span>
                      {hasDiscount && <span className="unique-regular-price text-muted text-decoration-line-through small">₹{regularPrice}</span>}
                      {item.weight > 0 && <span className="unique-weight-text text-muted ms-auto small">{item.weight} g</span>}
                    </div>

                    <div className="d-flex gap-2 mb-2 mt-auto">
                      <button
                        className="btn-unique-cart d-inline-flex align-items-center justify-content-center gap-1 btn btn-sm"
                        onClick={(e) => handleAddToCart(e, item)}
                      >
                        <BsCartPlus /> Cart
                      </button>
                      <button
                        className="btn-unique-buy d-inline-flex align-items-center justify-content-center gap-1 btn btn-sm"
                        onClick={(e) => handleBuyNow(e, item)}
                      >
                        <BsBagCheck /> Buy
                      </button>
                    </div>

                    <button
                      className="btn-unique-details d-inline-flex align-items-center justify-content-center gap-1 btn btn-sm"
                      onClick={() => handleViewDetails(item.product_id)}
                    >
                      View Details <BsArrowRight />
                    </button>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        <div className="text-center mt-4 pt-2">
          <Link to="/uniqueProducts" className="btn btn-outline-success rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow-sm">
            View All Unique Products <BsArrowRight />
          </Link>
        </div>
      </Container>
    </section>
  );
}
